import React, { useState } from 'react';
import { doc, getDoc, updateDoc, addDoc, collection, increment, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuth } from '../../hooks/AuthContext';
import { 
  Key, 
  CheckCircle, 
  AlertCircle,
  Building,
  Users,
  ArrowRight,
  Shield
} from 'lucide-react';
import { AccessCode } from '../../types/accessCodes';

interface AccessCodeEntryProps {
  onSuccess: (accessCode: AccessCode) => void;
  onSkip?: () => void;
  required?: boolean;
}

export const AccessCodeEntry: React.FC<AccessCodeEntryProps> = ({ 
  onSuccess, 
  onSkip, 
  required = false 
}) => {
  const { user } = useAuth();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validatedCode, setValidatedCode] = useState<AccessCode | null>(null);

  const validateAccessCode = async () => {
    if (!code.trim()) {
      setError('Please enter an access code');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Query for access code
      const accessCodesQuery = query(
        collection(db, 'accessCodes'),
        where('code', '==', code.toUpperCase().trim())
      );
      const accessCodesSnap = await getDocs(accessCodesQuery);

      if (accessCodesSnap.empty) {
        setError('Invalid access code. Please check with your department administrator.');
        return;
      }

      const accessCodeDoc = accessCodesSnap.docs[0];
      const accessCodeData = {
        id: accessCodeDoc.id,
        ...accessCodeDoc.data(),
        createdAt: accessCodeDoc.data().createdAt?.toDate(),
        updatedAt: accessCodeDoc.data().updatedAt?.toDate(),
        expiresAt: accessCodeDoc.data().expiresAt?.toDate(),
        lastUsedAt: accessCodeDoc.data().lastUsedAt?.toDate(),
      } as AccessCode;

      // Validate access code
      if (!accessCodeData.isActive) {
        setError('This access code has been disabled. Contact your administrator.');
        return;
      }

      if (accessCodeData.expiresAt && accessCodeData.expiresAt < new Date()) {
        setError('This access code has expired. Contact your administrator for a new one.');
        return;
      }

      if (accessCodeData.maxUses && accessCodeData.currentUses >= accessCodeData.maxUses) {
        setError('This access code has reached its usage limit. Contact your administrator.');
        return;
      }

      setValidatedCode(accessCodeData);

    } catch (error) {
      console.error('Error validating access code:', error);
      setError('Error validating access code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const confirmAndUseCode = async () => {
    if (!validatedCode || !user) return;

    setLoading(true);
    try {
      // Update access code usage count
      await updateDoc(doc(db, 'accessCodes', validatedCode.id), {
        currentUses: increment(1),
        lastUsedAt: new Date(),
        updatedAt: new Date()
      });

      // Create usage record
      await addDoc(collection(db, 'accessCodeUsage'), {
        accessCodeId: validatedCode.id,
        userId: user.uid,
        userEmail: user.email,
        userName: user.displayName || 'Unknown',
        departmentName: validatedCode.departmentName,
        usedAt: new Date(),
        ipAddress: '', // Could be populated from request
        userAgent: navigator.userAgent,
        modulesStarted: [],
        modulesCompleted: [],
        totalTimeSpent: 0,
        lastActivity: new Date(),
        loginCount: 1,
        journalEntries: 0
      });

      // Update user profile with department info
      await updateDoc(doc(db, 'users', user.uid), {
        accessCodeId: validatedCode.id,
        departmentName: validatedCode.departmentName,
        departmentType: validatedCode.customFields?.departmentType,
        region: validatedCode.customFields?.region,
        updatedAt: new Date()
      });

      onSuccess(validatedCode);

    } catch (error) {
      console.error('Error using access code:', error);
      setError('Error processing access code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (validatedCode) {
    return (
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 max-w-md mx-auto">
        <div className="text-center mb-6">
          <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Access Code Verified!</h2>
          <p className="text-gray-600">You're about to join:</p>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-6">
          <div className="flex items-center space-x-3 mb-4">
            <Building className="w-6 h-6 text-blue-600" />
            <div>
              <h3 className="font-bold text-blue-900">{validatedCode.departmentName}</h3>
              <p className="text-blue-700 text-sm">{validatedCode.customFields?.departmentType} Department</p>
            </div>
          </div>
          
          <div className="space-y-2 text-sm text-blue-800">
            <div className="flex items-center space-x-2">
              <Users className="w-4 h-4" />
              <span>Contact: {validatedCode.departmentContact}</span>
            </div>
            {validatedCode.customFields?.region && (
              <div className="flex items-center space-x-2">
                <Shield className="w-4 h-4" />
                <span>Region: {validatedCode.customFields.region}</span>
              </div>
            )}
          </div>
        </div>

        <div className="space-y-3">
          <button
            onClick={confirmAndUseCode}
            disabled={loading}
            className="w-full bg-green-600 text-white py-3 px-4 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center justify-center space-x-2"
          >
            {loading ? (
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
            ) : (
              <>
                <CheckCircle className="w-5 h-5" />
                <span>Join Department Training</span>
              </>
            )}
          </button>
          
          <button
            onClick={() => setValidatedCode(null)}
            className="w-full bg-gray-100 text-gray-700 py-3 px-4 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Use Different Code
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 max-w-md mx-auto">
      <div className="text-center mb-6">
        <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
          <Key className="w-8 h-8 text-blue-600" />
        </div>
        <h2 className="text-xl font-bold text-gray-900 mb-2">
          {required ? 'Access Code Required' : 'Department Access Code'}
        </h2>
        <p className="text-gray-600">
          {required 
            ? 'Enter your department access code to continue'
            : 'Enter your department access code to access training modules'
          }
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-center space-x-2">
            <AlertCircle className="w-5 h-5 text-red-600" />
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        </div>
      )}

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Access Code
          </label>
          <input
            type="text"
            value={code}
            onChange={(e) => {
              setCode(e.target.value.toUpperCase());
              setError(null);
            }}
            placeholder="Enter your department code"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-center text-lg tracking-wider"
            maxLength={20}
          />
          <p className="text-xs text-gray-500 mt-1">
            Example: LAFD2024ABCD or NYPD-WELLNESS
          </p>
        </div>

        <button
          onClick={validateAccessCode}
          disabled={loading || !code.trim()}
          className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
        >
          {loading ? (
            <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
          ) : (
            <>
              <Key className="w-5 h-5" />
              <span>Validate Code</span>
            </>
          )}
        </button>

        {!required && onSkip && (
          <button
            onClick={onSkip}
            className="w-full bg-gray-100 text-gray-700 py-3 px-4 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Skip for Now
          </button>
        )}
      </div>

      <div className="mt-6 bg-gray-50 rounded-lg p-4">
        <h3 className="font-medium text-gray-900 mb-2">Don't have an access code?</h3>
        <p className="text-sm text-gray-600 mb-3">
          Contact your department administrator or training coordinator to get your access code.
        </p>
        <div className="text-xs text-gray-500">
          <p>• Access codes are provided by your department</p>
          <p>• Codes track your department's training progress</p>
          <p>• Help us provide analytics to your leadership</p>
        </div>
      </div>
    </div>
  );
};
